import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Drawer,
  Typography,
  TextField,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Chip,
  Menu,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search,
  FilterList,
  Close,
  ExpandMore,
  ChevronRight,
  FolderOpen,
  Folder,
  LocationOn,
  Route,
  DeviceHub,
  DataObject,
  BarChart,
  Warning,
  Assessment,
  Info,
  Map,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import cmpLogo from "../../src/pc_logo.ico";
import { Circle } from "lucide-react";
import { FiberManualRecord } from "@mui/icons-material";

// Helper function to get clean name for sorting
const getCleanNameForSorting = (name, type) => {
  if (type === "group" && /^prc\s*-/i.test(name)) {
    return name.replace(/^prc\s*-\s*/i, "").trim();
  }
  return name.trim();
};

const trimStartWithEllipsis = (text, maxChars) => {
  if (!text) return "";
  if (text.length <= maxChars) return text;
  return "..." + text.slice(text.length - maxChars);
};

/**
 * Strip a parent prefix from a child name (case-insensitive).
 * e.g. parentName="San Angelo", childName="San Angelo North" → "North"
 * If the child doesn't start with the parent name, return as-is.
 */
const stripParentPrefix = (childName, parentName) => {
  if (!childName || !parentName) return childName;
  const escaped = parentName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`^${escaped}\\s*`, "i");
  const stripped = childName.replace(regex, "").trim();
  // Only use the stripped version if something remains
  return stripped.length > 0 ? stripped : childName;
};

/**
 * Get the "effective" group ancestor name to strip from region/route labels.
 * We pass `ancestorGroupName` down the tree so that nested regions/routes
 * can all strip the top-level group prefix.
 */

// Custom Tree Node Component
const TreeNode = ({
  node,
  level = 0,
  search,
  status,
  type,
  onDeviceSelect,
  selectedDevice,
  onNavigate,
  collapseTrigger,
  expandTrigger,
  ancestorGroupName, // <-- NEW: the nearest group ancestor's display name
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [nodeMenuAnchor, setNodeMenuAnchor] = useState(null);

  // Collapse all nodes when collapseTrigger changes
  React.useEffect(() => {
    if (collapseTrigger > 0) {
      setExpanded(false);
    }
  }, [collapseTrigger]);

  React.useEffect(() => {
    if (expandTrigger && node.type !== "device") {
      const targetId =
        typeof expandTrigger === "object" ? expandTrigger.id : expandTrigger;

      const hasDeviceWithId = (n, targetId) => {
        if (n.type === "device" && n.id === targetId) {
          return true;
        }
        return (
          n.children?.some((child) => hasDeviceWithId(child, targetId)) || false
        );
      };

      if (hasDeviceWithId(node, targetId)) {
        setExpanded(true);
      }
    }
  }, [expandTrigger, node]);

  const getMatchingDeviceCount = useMemo(() => {
    const collectMatchingDevices = (n, deviceSet = new Set()) => {
      if (n.type === "device") {
        const nameMatch =
          !search || n.name.toLowerCase().includes(search.toLowerCase());
        const statusMatch = !status || n.status === status;
        const typeMatch = !type || n.deviceType === type;
        if (nameMatch && statusMatch && typeMatch) {
          deviceSet.add(n.id);
        }
        return deviceSet;
      }

      (n.children || []).forEach((child) =>
        collectMatchingDevices(child, deviceSet),
      );
      return deviceSet;
    };

    return collectMatchingDevices(node).size;
  }, [node, search, status, type]);

  const getTotalDeviceCount = useMemo(() => {
    const collectAllDevices = (n, deviceSet = new Set()) => {
      if (n.type === "device") {
        deviceSet.add(n.id);
        return deviceSet;
      }
      (n.children || []).forEach((child) =>
        collectAllDevices(child, deviceSet),
      );
      return deviceSet;
    };

    return collectAllDevices(node).size;
  }, [node]);

  React.useEffect(() => {
    if (search && node.type !== "device") {
      const hasMatchingDevice = (n) => {
        if (
          n.type === "device" &&
          n.name.toLowerCase().includes(search.toLowerCase())
        ) {
          return true;
        }
        return n.children?.some((child) => hasMatchingDevice(child)) || false;
      };

      if (hasMatchingDevice(node)) {
        setExpanded(true);
      }
    }
  }, [search, node]);

  const nodeMatches = useMemo(() => {
    const checkNode = (n) => {
      const nameMatch =
        !search || n.name.toLowerCase().includes(search.toLowerCase());
      const statusMatch = !status || n.status === status;
      const typeMatch =
        !type || (n.type === "device" ? n.deviceType === type : true);

      if (n.type === "device") {
        return nameMatch && statusMatch && typeMatch;
      }

      const childrenMatch =
        n.children?.some((child) => checkNode(child)) || false;

      if (!search && !status && !type) {
        return true;
      }

      if (search && !status && !type) {
        return nameMatch || childrenMatch;
      }

      return childrenMatch;
    };

    return checkNode(node);
  }, [node, search, status, type]);

  // Sort children alphabetically using clean names
  const sortedChildren = useMemo(() => {
    if (!node.children || node.children.length === 0) return [];

    return [...node.children].sort((a, b) => {
      const nameA = getCleanNameForSorting(a.name, a.type);
      const nameB = getCleanNameForSorting(b.name, b.type);

      return nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [node.children]);

  if (!nodeMatches) return null;

  const getIcon = () => {
    switch (node.type) {
      case "group":
        return expanded ? <FolderOpen /> : <Folder />;
      case "region":
        return <LocationOn />;
      case "route":
        return <Route />;
      case "device":
        return <FiberManualRecord sx={{ fontSize: 12 }} />;
      default:
        return <Folder />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "online":
        return "#10b981";
      case "offline":
        return "#6b7280";
      case "alarm":
        return "#ef4444";
      case "no contact":
        return "#964B00";
      case "invalid data":
        return "#f59e0b";
      case "standby":
        return "#9616d1ff";
      default:
        return "#6b7280";
    }
  };

  const hasChildren = node.children && node.children.length > 0;
  const canExpand = hasChildren && node.type !== "device";
  const isExpandableRegionOrRoute =
    node.type === "region" || node.type === "route";

  // Check if this is an empty region or route
  const isEmptyRegionOrRoute =
    (node.type === "region" || node.type === "route") && !hasChildren;

  const handleNodeClick = (event) => {
    if (node.type === "device") {
      setNodeMenuAnchor(event.currentTarget);
    } else if (
      node.type === "region" ||
      node.type === "route" ||
      node.type === "group" ||
      canExpand
    ) {
      setExpanded(!expanded);
    }
  };

  const handleNodeMenuClose = () => {
    setNodeMenuAnchor(null);
  };

  const handleMapClick = () => {
    handleNodeMenuClose();
    navigate(`view-on-map`, {
      state: { type: node.type, id: node.id, name: node.name },
    });
  };

  const handleMenuItemClick = (page) => {
    handleNodeMenuClose();
    onDeviceSelect(node);
    if (onNavigate) {
      console.log(page, "page");
      onNavigate(node, page);
    }
  };

  const isSelected =
    selectedDevice && selectedDevice.id === node.id && node.type === "device";

  const shouldShowCount = node.type !== "device" && getTotalDeviceCount > 0;

  const getCountDisplay = () => {
    if (!shouldShowCount) return null;

    const hasActiveFilters = search || status || type;

    if (hasActiveFilters) {
      return `${getMatchingDeviceCount}/${getTotalDeviceCount}`;
    } else {
      return getTotalDeviceCount.toString();
    }
  };

  const countDisplay = getCountDisplay();

  const deviceMenuItems = [
    {
      label: "Field Data",
      icon: <DataObject fontSize="small" />,
      page: "Engine Data",
    },
    { label: "Charts", icon: <BarChart fontSize="small" />, page: "Charts" },
    { label: "Alarms", icon: <Warning fontSize="small" />, page: "Alarms" },
    {
      label: "Runtime Report",
      icon: <Assessment fontSize="small" />,
      page: "Runtime Report",
    },
    {
      label: "Unit Information",
      icon: <Info fontSize="small" />,
      page: "Unit Information",
    },
  ];

  const showMapInMenu = ["group", "region", "route"].includes(node.type);
  const shouldShowCollapse = canExpand || isEmptyRegionOrRoute;

  // ─── Compute the display label ────────────────────────────────────────────
  // Groups: strip "prc -" prefix as before
  // Regions / Routes: additionally strip the ancestor group name prefix
  const getDisplayName = () => {
    let name = node.name;

    if (node.type === "group") {
      // Strip "prc -" prefix
      if (/^prc\s*-/i.test(name)) {
        name = name.replace(/^prc\s*-\s*/i, "");
      }
      return name;
    }

    if (node.type === "region" || node.type === "route") {
      // Strip ancestor group name prefix if present
      if (ancestorGroupName) {
        name = stripParentPrefix(name, ancestorGroupName);
      }
      return name;
    }

    // Devices: return as-is (truncation handled by CSS)
    return name;
  };

  const displayName = getDisplayName();

  // Determine what ancestorGroupName to pass down to children:
  // If current node is a group, pass its raw (post-prc-strip) name.
  // Otherwise propagate whatever came in.
  const childAncestorGroupName =
    node.type === "group"
      ? node.name.replace(/^prc\s*-\s*/i, "").trim()
      : ancestorGroupName;

  return (
    <>
      <ListItem
        sx={{
          pl: 0.2 + level * 2,
          py: 0.5,
          cursor:
            canExpand || node.type === "device" || isEmptyRegionOrRoute
              ? "pointer"
              : "default",
          backgroundColor: isSelected ? "#374151" : "transparent",
          "&:hover": {
            backgroundColor: isSelected ? "#4b5563" : "#374151",
          },
          borderLeft: isSelected ? "3px solid #000000ff" : "none",
        }}
        onClick={handleNodeClick}
      >
        <ListItemIcon
          sx={{
            marginLeft: 1,
            minWidth: 25,
            color:
              node.type === "device" ? getStatusColor(node.status) : "#9ca3af",
          }}
        >
          {getIcon()}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize:
                    node.type === "region"
                      ? "0.85rem"
                      : node.type === "route"
                        ? "0.78rem"
                        : node.type === "device"
                          ? "0.7rem"
                          : "1rem",
                  fontWeight:
                    node.type === "group"
                      ? 600
                      : node.type === "region"
                        ? 100
                        : node.type === "route"
                          ? 100
                          : node.type === "device"
                            ? isSelected
                              ? 600
                              : 400
                            : 500,
                  // ── FIX: trailing ellipsis instead of leading ──
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  // direction & textAlign removed — they caused leading "..."
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {displayName}
              </Typography>

              {countDisplay && (
                <Chip
                  label={countDisplay}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    height: 21,
                    fontWeight: 800,
                    fontSize: "0.6rem",
                    backgroundColor:
                      search || status || type
                        ? getStatusColor(status)
                        : "#074dbdff",
                    color: "#ffffffff",
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              )}
            </Box>
          }
        />
        {(canExpand || (isExpandableRegionOrRoute && !hasChildren)) && (
          <ListItemIcon sx={{ minWidth: 0, color: "#af9ca7ff" }}>
            {expanded ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </ListItemIcon>
        )}

        {isSelected && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#fff",
              mr: 1,
            }}
          />
        )}
      </ListItem>

      {/* Menu for devices */}
      {node.type === "device" && (
        <Menu
          anchorEl={nodeMenuAnchor}
          open={Boolean(nodeMenuAnchor)}
          onClose={handleNodeMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: "#1f2937",
              border: "1px solid #374151",
              minWidth: 180,
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #374151" }}>
            <Typography
              variant="subtitle2"
              sx={{ color: getStatusColor(node.status), fontWeight: 600 }}
            >
              {node.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              {node.status} • ID: {node.id}
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: "#374151" }} />

          {deviceMenuItems.map((item) => (
            <MenuItem
              key={item.page}
              onClick={() => handleMenuItemClick(item.page)}
              sx={{
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#374151",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#9ca3af", minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </MenuItem>
          ))}
        </Menu>
      )}

      {shouldShowCollapse && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {showMapInMenu && (
            <>
              <ListItem
                sx={{
                  pl: hasChildren
                    ? 2.5 + (level + 1) * 2
                    : 5.5 + (level + 1) * 2,
                  py: 0.5,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#374151",
                  },
                }}
                onClick={handleMapClick}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#10b17c" }}>
                  <Map fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        color: "#10b17c",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      }}
                    >
                      Home View
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem
                sx={{
                  pl: hasChildren
                    ? 2.5 + (level + 1) * 2
                    : 5.5 + (level + 1) * 2,
                  py: 0.5,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#374151",
                  },
                }}
                onClick={() => {
                  const collectDeviceIds = (n) => {
                    const ids = [];
                    if (n.type === "device") {
                      ids.push(n.id);
                    }
                    (n.children || []).forEach((child) => {
                      ids.push(...collectDeviceIds(child));
                    });
                    return ids;
                  };

                  const deviceIds = collectDeviceIds(node);

                  navigate(`reports/monthly`, {
                    state: {
                      type: node.type,
                      id: node.id,
                      name: node.name,
                      deviceIds: deviceIds,
                      filterApplied: true,
                    },
                  });
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#18b8e0ff" }}>
                  <Assessment fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        color: "#18b8e0ff",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Report
                    </Typography>
                  }
                />
              </ListItem>
            </>
          )}

          {sortedChildren.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              search={search}
              status={status}
              type={type}
              onDeviceSelect={onDeviceSelect}
              selectedDevice={selectedDevice}
              onNavigate={onNavigate}
              collapseTrigger={collapseTrigger}
              expandTrigger={expandTrigger}
              ancestorGroupName={childAncestorGroupName} // <-- pass down
            />
          ))}
        </Collapse>
      )}
    </>
  );
};

export default function Sidebar({
  loading,
  vendingMachineData,
  onDeviceSelect,
  selectedDevice,
  onNavigate,
  mobileOpen,
  onMobileClose,
  deviceIdToExpand,
}) {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "online":
        return "#10b981";
      case "offline":
        return "#6b7280";
      case "alarm":
        return "#ef4444";
      case "no contact":
        return "#964B00";
      case "invalid data":
        return "#f59e0b";
      case "standby":
        return "#9616d1ff";
      default:
        return "#6b7280";
    }
  };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [collapseTrigger, setCollapseTrigger] = useState(0);

  const [expandTrigger, setExpandTrigger] = useState(null);

  useEffect(() => {
    if (deviceIdToExpand && vendingMachineData) {
      handleClearAll();
      setExpandTrigger({ id: deviceIdToExpand, timestamp: Date.now() });
    }
  }, [deviceIdToExpand, vendingMachineData]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = JSON.parse(localStorage.getItem("user"));

  const sortedVendingMachineData = useMemo(() => {
    if (!vendingMachineData || vendingMachineData.length === 0)
      return vendingMachineData;

    return [...vendingMachineData].sort((a, b) => {
      const nameA = getCleanNameForSorting(a.name, a.type);
      const nameB = getCleanNameForSorting(b.name, b.type);

      return nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [vendingMachineData]);

  const getTotalMatchingDevices = useMemo(() => {
    if (!vendingMachineData) return 0;

    const collectMatchingDevices = (node, deviceSet = new Set()) => {
      if (node.type === "device") {
        const nameMatch =
          !search || node.name.toLowerCase().includes(search.toLowerCase());
        const statusMatch = !status || node.status === status;
        const typeMatch = !type || node.deviceType === type;
        if (nameMatch && statusMatch && typeMatch) {
          deviceSet.add(node.id);
        }
        return deviceSet;
      }

      (node.children || []).forEach((child) =>
        collectMatchingDevices(child, deviceSet),
      );
      return deviceSet;
    };
    const allMatchingDevices = new Set();
    vendingMachineData.forEach((rootNode) =>
      collectMatchingDevices(rootNode, allMatchingDevices),
    );
    return allMatchingDevices.size;
  }, [vendingMachineData, search, status, type]);

  const getTotalDevices = useMemo(() => {
    if (!vendingMachineData) return 0;

    const collectAllDevices = (node, deviceSet = new Set()) => {
      if (node.type === "device") {
        deviceSet.add(node.id);
        return deviceSet;
      }
      (node.children || []).forEach((child) =>
        collectAllDevices(child, deviceSet),
      );
      return deviceSet;
    };

    const allDevices = new Set();
    vendingMachineData.forEach((rootNode) =>
      collectAllDevices(rootNode, allDevices),
    );
    return allDevices.size;
  }, [vendingMachineData]);

  const handleClearAll = useCallback(() => {
    setStatus("");
    setType("");
    setSearch("");
    setCollapseTrigger((prev) => prev + 1);
  }, []);

  const drawerContent = (
    <>
      <Box
        sx={{
          height: "80px",
          minHeight: "80px",
          maxHeight: "80px",
          borderBottom: "1px solid #374151",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <img
          src={cmpLogo}
          alt="Logo"
          style={{ height: "auto", width: "98%", maxHeight: "70px" }}
        />
      </Box>

      <Box sx={{ p: 2, borderBottom: "1px solid #374151" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Search fontSize="small" />
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Devices.."
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: { color: "#fff", flex: 1 },
            }}
            fullWidth
          />
          <IconButton
            onClick={() => setFiltersOpen(!filtersOpen)}
            sx={{ color: filtersOpen ? "#9ca3af" : "#fff" }}
          >
            <FilterList />
          </IconButton>
        </Box>

        {vendingMachineData && (
          <Box
            sx={{
              mt: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              Unique Devices:{" "}
              {search || status || type
                ? `${getTotalMatchingDevices}/${getTotalDevices}`
                : getTotalDevices}
            </Typography>
            {(search || status || type) &&
              getTotalMatchingDevices !== getTotalDevices && (
                <Typography variant="caption" sx={{ color: "#45c436" }}>
                  {getTotalMatchingDevices} found
                </Typography>
              )}
          </Box>
        )}

        <Collapse in={filtersOpen}>
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="subtitle1">Filters</Typography>
              <Button
                size="small"
                startIcon={
                  <Close
                    sx={{
                      color: "#dcdcdc",
                      fontWeight: 500,
                      fontSize: 18,
                    }}
                  />
                }
                onClick={handleClearAll}
                sx={{
                  color: "#e0e0e0",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.5,
                  transition: "all 0.2s ease",
                  "& .MuiButton-startIcon": {
                    marginRight: "3px",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    color: "#ffffff",
                    "& .MuiSvgIcon-root": {
                      color: "#ffffff",
                    },
                  },
                }}
              >
                Clear all
              </Button>
            </Box>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel sx={{ color: "#fff" }}>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                sx={{ color: "#fff" }}
                MenuProps={{
                  disablePortal: false,
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="alarm">Alarm</MenuItem>
                <MenuItem value="invalid data">Invalid Data</MenuItem>
                <MenuItem value="no contact">No Contact</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "#fff" }}>Controller</InputLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value)}
                sx={{ color: "#fff" }}
                MenuProps={{
                  disablePortal: false,
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CMP0">CMP0</MenuItem>
                <MenuItem value="C5E">C5E</MenuItem>
                <MenuItem value="C5">C5</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Collapse>
      </Box>
      {selectedDevice && (
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #374151",
            backgroundColor: "#111827",
          }}
        >
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            Selected Device
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: getStatusColor(selectedDevice.status),
              fontWeight: 600,
            }}
          >
            {selectedDevice.name}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            ID: {selectedDevice.id}
          </Typography>
        </Box>
      )}

      {!vendingMachineData || loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <CircularProgress color="#60a5fa" size={70} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {sortedVendingMachineData && sortedVendingMachineData.length > 0 ? (
            <List sx={{ p: 0 }}>
              {sortedVendingMachineData.map((rootNode) => (
                <TreeNode
                  key={rootNode.id}
                  node={rootNode}
                  search={search}
                  status={status}
                  type={type}
                  onDeviceSelect={onDeviceSelect}
                  selectedDevice={selectedDevice}
                  onNavigate={onNavigate}
                  collapseTrigger={collapseTrigger}
                  expandTrigger={expandTrigger}
                  ancestorGroupName={null} // root nodes have no ancestor group yet
                />
              ))}
            </List>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#9ca3af", textAlign: "center" }}
              >
                No devices available
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: 280,
              backgroundColor: "#1f2937",
              color: "#fff",
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            width: 280,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: 280,
              backgroundColor: "#1f2937",
              color: "#fff",
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
}